// Local heuristic annotation interpreter (no external API).
// Reads free-form clinical text and returns a reviewable draft.
import catalogRaw from "./effectCatalog.json";

export type EffectPolarity = "benefit" | "adverse" | "bidirectional";
export interface EffectCatalogEntry {
  key: string;
  label: string;
  axis: string;
  polarity: EffectPolarity;
  benefit_prompt?: string;
  adverse_prompt?: string;
}

export const EFFECT_CATALOG: EffectCatalogEntry[] = catalogRaw as EffectCatalogEntry[];
export const EFFECT_BY_KEY: Record<string, EffectCatalogEntry> = Object.fromEntries(EFFECT_CATALOG.map((e) => [e.key, e]));

export interface InterpretedEffect {
  effect_key: string;
  label: string;
  value: number; // 0-100
  reason: string;
  polarity: "benefit" | "adverse";
}

export interface InterpretedAnnotation {
  benefits: InterpretedEffect[];
  adverse: InterpretedEffect[];
  mechanism: string[];
  confidence: number;
  review_status: "rascunho_ia_local";
}

// Keyword lexicon → effect_key mapping.
// Each hit contributes to a benefit or adverse bucket depending on context.
interface Rule {
  patterns: RegExp[];
  effect_key: string;
  polarity: "benefit" | "adverse" | "context";
  base: number;
  reason: string;
}

const RULES: Rule[] = [
  // Sleep
  { patterns: [/\bdorm(ir|e|iu)\b/i, /\bsono\b/i, /\binduz(ir|e).*sono\b/i, /\bsedaç[aã]o\b/i, /\bsonolent/i], effect_key: "sleep_induction", polarity: "benefit", base: 65, reason: "induz/ajuda sono" },
  { patterns: [/sedaç[aã]o excess/i, /muito sedad/i, /ressaca/i], effect_key: "sedation_excess", polarity: "adverse", base: 65, reason: "sedação excessiva" },
  { patterns: [/\bins[oô]nia\b/i, /\bn[ãa]o.*consegue.*dorm/i, /piora.*sono/i], effect_key: "insomnia_risk", polarity: "adverse", base: 70, reason: "risco/piora de insônia" },
  { patterns: [/qualidade.*sono/i, /sono repar/i], effect_key: "sleep_quality", polarity: "benefit", base: 55, reason: "melhora qualidade do sono" },

  // Anxiety autonomic
  { patterns: [/taquicard/i, /tremor/i, /sudore/i, /descarga.*autonom/i, /descarga.*adren/i, /ansiedade.*f[ií]sica/i, /aperto tor[aá]cico/i], effect_key: "autonomic_anxiety", polarity: "context", base: 60, reason: "sinais autonômicos" },
  // Cognitive anxiety / rumination
  { patterns: [/preocupaç[aã]o/i, /antecipaç[aã]o ansiosa/i, /ansiedade cognitiv/i], effect_key: "cognitive_anxiety", polarity: "context", base: 55, reason: "ansiedade cognitiva" },
  { patterns: [/rumina/i, /perseveraç/i], effect_key: "rumination", polarity: "context", base: 55, reason: "ruminação" },
  { patterns: [/ansiedade social/i, /fobia social/i], effect_key: "social_anxiety", polarity: "benefit", base: 55, reason: "ansiedade social" },

  // Affect
  { patterns: [/melhora.*humor/i, /humor.*melhor/i, /vitalidade/i], effect_key: "mood_elevation", polarity: "benefit", base: 55, reason: "melhora humor" },
  { patterns: [/depress[aã]o/i, /anedon/i, /anergia/i, /apati/i], effect_key: "depression", polarity: "context", base: 55, reason: "depressão/anergia" },
  { patterns: [/irritab/i, /impaci[eê]nc/i, /agressiv/i], effect_key: "irritability", polarity: "context", base: 55, reason: "irritabilidade" },
  { patterns: [/labil/i, /oscilaç[aã]o afetiva/i, /explos[aã]o/i], effect_key: "emotional_lability", polarity: "context", base: 55, reason: "labilidade" },
  { patterns: [/embotament/i, /apatia/i, /achatad/i], effect_key: "blunting_apathy", polarity: "adverse", base: 55, reason: "embotamento/apatia" },

  // Activation
  { patterns: [/hiperativ/i, /agitaç[aã]o/i, /inquiet/i], effect_key: "hyperactivity_agitation", polarity: "context", base: 55, reason: "hiperatividade/agitação" },
  { patterns: [/reduz.*hiperativ/i, /diminui.*hiperativ/i], effect_key: "physical_hyperactivity_reduction", polarity: "benefit", base: 60, reason: "reduz hiperatividade física" },

  // Executive
  { patterns: [/iniciar tarefa/i, /procrastin/i, /come[çc]ar.*tarefa/i], effect_key: "task_initiation", polarity: "benefit", base: 60, reason: "iniciar tarefas" },
  { patterns: [/sustentar tarefa/i, /manter.*foco/i, /manter tarefa/i], effect_key: "task_sustainment", polarity: "benefit", base: 60, reason: "sustentar tarefas" },
  { patterns: [/foco produtiv/i, /concentra[çc][aã]o produtiv/i], effect_key: "productive_focus", polarity: "benefit", base: 65, reason: "foco produtivo" },
  { patterns: [/hiperfoco est[eé]ril/i, /travad?o?.*celular/i, /hiperfoco improdut/i], effect_key: "sterile_hyperfocus", polarity: "adverse", base: 65, reason: "hiperfoco estéril" },
  { patterns: [/mem[oó]ria de trabalho/i, /working memory/i], effect_key: "working_memory", polarity: "context", base: 55, reason: "memória de trabalho" },
  { patterns: [/velocidade.*process/i, /lentificaç[aã]o cognitiv/i], effect_key: "processing_speed", polarity: "context", base: 50, reason: "velocidade de processamento" },
  { patterns: [/flexibilidade cognit/i, /rigidez cognit/i], effect_key: "cognitive_flexibility", polarity: "context", base: 50, reason: "flexibilidade cognitiva" },
  { patterns: [/funç[aã]o executiv/i, /execuç[aã]o global/i], effect_key: "executive_function_global", polarity: "benefit", base: 60, reason: "função executiva" },
  { patterns: [/piora.*cognit/i, /nebulinha/i, /brain fog/i], effect_key: "cognitive_worsening", polarity: "adverse", base: 55, reason: "piora cognitiva" },
  { patterns: [/rigidez cognit/i], effect_key: "cognitive_rigidity", polarity: "adverse", base: 55, reason: "rigidez cognitiva" },

  // Motivation
  { patterns: [/motivaç[aã]o/i, /\bdrive\b/i], effect_key: "motivation_drive", polarity: "benefit", base: 55, reason: "motivação/drive" },

  // Craving / dependence
  { patterns: [/craving/i, /fissura/i], effect_key: "craving", polarity: "context", base: 60, reason: "craving" },
  { patterns: [/redose/i, /dose extra/i, /repetir a dose/i], effect_key: "redose_risk", polarity: "adverse", base: 65, reason: "risco de redose" },
  { patterns: [/impulsividade/i, /recompensa imediat/i], effect_key: "impulsivity", polarity: "context", base: 55, reason: "impulsividade" },
  { patterns: [/compuls[aã]o/i, /compulsivid/i], effect_key: "compulsivity", polarity: "adverse", base: 60, reason: "compulsividade" },

  // Metabolic / somatic
  { patterns: [/perda.*apetite/i, /anorexia/i, /sem fome/i], effect_key: "appetite_loss", polarity: "adverse", base: 65, reason: "perda de apetite" },
  { patterns: [/ganho.*peso/i, /peso subiu/i, /obesidade/i], effect_key: "metabolic_gain", polarity: "adverse", base: 60, reason: "ganho metabólico" },
  { patterns: [/hipotens[aã]o|ortost[aá]tic|tontura ao levantar/i], effect_key: "orthostasis", polarity: "adverse", base: 65, reason: "hipotensão ortostática" },
  { patterns: [/press[aã]o (arterial|alta|baixa)/i, /\bPA\b/, /frequ[eê]ncia card|\bFC\b/], effect_key: "blood_pressure_heart_rate", polarity: "context", base: 55, reason: "PA/FC alterada" },
  { patterns: [/c[oó]lica/i, /n[aá]usea/i, /diarreia/i, /vom/i, /\bGI\b/i], effect_key: "gi_colic", polarity: "adverse", base: 55, reason: "sintomas GI" },
  { patterns: [/dor\b/i, /somatiz/i], effect_key: "pain_somatic", polarity: "benefit", base: 50, reason: "dor/somatização" },
  { patterns: [/libido/i, /sexual/i, /disfunç[aã]o sexual/i], effect_key: "sexual_function", polarity: "adverse", base: 55, reason: "função sexual" },

  // Severe safety
  { patterns: [/paranoia|paran[oó]id|ideias de refer[eê]ncia|psicose|psicotomim/i], effect_key: "psychotomimetic", polarity: "adverse", base: 65, reason: "psicotomimético/paranoia" },
  { patterns: [/serotoninerg|serotonin[eé]rgic/i], effect_key: "serotonergic_risk", polarity: "adverse", base: 60, reason: "risco serotoninérgico" },
  { patterns: [/depress[aã]o respirat[oó]ria|apneia/i], effect_key: "respiratory_sedation_risk", polarity: "adverse", base: 70, reason: "risco sedativo/respiratório" },
];

// Mechanism / MoA lexicon
const MECH_RULES: Array<{ patterns: RegExp[]; label: string }> = [
  { patterns: [/alfa[- ]?2/i, /agonismo alfa/i], label: "agonismo alfa-2 / redução noradrenérgica" },
  { patterns: [/noradren[eé]rg|noradrenalina/i], label: "modulação noradrenérgica" },
  { patterns: [/dopamin[eé]rg|dopamina/i], label: "modulação dopaminérgica" },
  { patterns: [/serotonin[eé]rg|serotonina|\bISRS\b|\bSSRI\b/i], label: "modulação serotoninérgica" },
  { patterns: [/gaba|benzo|\bBZD\b/i], label: "potencialização GABAérgica" },
  { patterns: [/glutamat|NMDA/i], label: "modulação glutamatérgica" },
  { patterns: [/anticolin[eé]rg|muscar[ií]nic/i], label: "efeito anticolinérgico" },
  { patterns: [/histamin[eé]rgic|H1/i], label: "bloqueio H1 / sedação histaminérgica" },
  { patterns: [/pr[oó][- ]f[aá]rmaco|prodrug|bioativa[çc][aã]o/i], label: "pró-fármaco com bioativação" },
  { patterns: [/CYP\s*\d/i, /citocromo/i], label: "metabolismo hepático via CYP" },
];

// If a line contains a "reduce/melhora" verb, treat context effects as benefit.
// If it contains "piora/causa", treat as adverse.
function classifySegment(seg: string, base: "context"): "benefit" | "adverse" {
  if (/piora|induz|causa|provoca|aumenta|gera|risco de|efeito colateral|adverso/i.test(seg)) return "adverse";
  if (/reduz|diminui|melhora|ajuda|al[ií]via|estabiliza|atenua|controla|acalma/i.test(seg)) return "benefit";
  return "benefit";
}

export function interpretAnnotation(text: string): InterpretedAnnotation {
  const clean = String(text || "").trim();
  if (!clean) return { benefits: [], adverse: [], mechanism: [], confidence: 0, review_status: "rascunho_ia_local" };

  // Segment by clause separators to attribute polarity per-clause
  const segments = clean.split(/(?:[.;\n]|\bmas\b|\bpor[eé]m\b|\bcontudo\b|\bem contrapartida\b)/i).map((s) => s.trim()).filter(Boolean);

  const benefits = new Map<string, InterpretedEffect>();
  const adverse = new Map<string, InterpretedEffect>();

  for (const seg of segments) {
    for (const rule of RULES) {
      if (!rule.patterns.some((r) => r.test(seg))) continue;
      const catalog = EFFECT_BY_KEY[rule.effect_key];
      const label = catalog?.label ?? rule.effect_key;
      let pol: "benefit" | "adverse";
      if (rule.polarity === "benefit") pol = "benefit";
      else if (rule.polarity === "adverse") pol = "adverse";
      else pol = classifySegment(seg, "context");
      const bucket = pol === "benefit" ? benefits : adverse;
      const prior = bucket.get(rule.effect_key);
      const value = Math.max(prior?.value ?? 0, rule.base);
      bucket.set(rule.effect_key, {
        effect_key: rule.effect_key,
        label,
        value,
        reason: prior?.reason ? `${prior.reason}; ${rule.reason}` : rule.reason,
        polarity: pol,
      });
    }
  }

  const mechanism: string[] = [];
  for (const m of MECH_RULES) if (m.patterns.some((r) => r.test(clean))) mechanism.push(m.label);

  const total = benefits.size + adverse.size;
  const confidence = Math.min(90, 20 + total * 8 + mechanism.length * 5);

  return {
    benefits: Array.from(benefits.values()).sort((a, b) => b.value - a.value),
    adverse: Array.from(adverse.values()).sort((a, b) => b.value - a.value),
    mechanism,
    confidence,
    review_status: "rascunho_ia_local",
  };
}
